/**
 * ChoirMemberPortal - Login portal for approved choir members
 * Features: Login with phone/PIN, view rehearsals, see hymn selections
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Music,
  User,
  Phone,
  Lock,
  LogOut,
  Calendar,
  Clock,
  MapPin,
  BookOpen,
  Users,
  Bell,
  ChevronRight,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Music2,
  Mic2,
  FileText
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const ChoirMemberPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Login form
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  
  // Data states
  const [rehearsals, setRehearsals] = useState([]);
  const [upcomingServices, setUpcomingServices] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Check for existing session
  useEffect(() => {
    const savedMember = localStorage.getItem('choir_member');
    if (savedMember) {
      try {
        const parsed = JSON.parse(savedMember);
        setMember(parsed);
        setIsLoggedIn(true);
      } catch (e) {
        localStorage.removeItem('choir_member');
      }
    }
  }, []);

  // Load data when logged in
  useEffect(() => {
    if (isLoggedIn) {
      loadChoirData();
    }
  }, [isLoggedIn]);

  const loadChoirData = async () => {
    try {
      const [rehearsalsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/choir/rehearsals`),
        axios.get(`${API}/choir/upcoming-services`)
      ]);
      setRehearsals(rehearsalsRes.data || []);
      setUpcomingServices(servicesRes.data || []);
    } catch (error) {
      console.error('Error loading choir data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/choir/member-login`, { phone, pin });
      if (res.data.success) {
        setMember(res.data.member);
        setIsLoggedIn(true);
        localStorage.setItem('choir_member', JSON.stringify(res.data.member));
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed. Please check your credentials.');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setMember(null);
    localStorage.removeItem('choir_member');
    setPhone('');
    setPin('');
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo & Header */}
          <div className="text-center mb-8">
            <motion.div
              className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-2xl"
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
            >
              <Music className="w-10 h-10 text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-2">Choir Portal</h1>
            <p className="text-purple-200">CCC Valley of Berachah Parish</p>
          </div>

          {/* Login Card */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            <form onSubmit={handleLogin} className="space-y-5">
              {/* Phone Input */}
              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Enter your phone number"
                    className="w-full pl-12 pr-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
              </div>

              {/* PIN Input */}
              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  4-Digit PIN
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.slice(0, 4))}
                    placeholder="Enter your PIN"
                    maxLength={4}
                    className="w-full pl-12 pr-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
                <p className="text-purple-300/70 text-xs mt-2">
                  Default PIN is the last 4 digits of your phone number
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-200 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-purple-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    <User className="w-5 h-5" />
                    Sign In to Portal
                  </>
                )}
              </motion.button>
            </form>

            {/* Help Text */}
            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-purple-300/70 text-sm">
                Not a choir member yet?{' '}
                <a href="#" className="text-purple-300 hover:text-white transition-colors">
                  Apply to join
                </a>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Member Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <Music className="w-6 h-6" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Choir Portal</h1>
                <p className="text-purple-200 text-sm">Welcome, {member?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm">
                <Mic2 className="w-4 h-4" />
                {member?.voice_part || 'Member'}
              </span>
              <button
                onClick={handleLogout}
                className="p-2 bg-white/20 rounded-xl hover:bg-white/30 transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: User },
              { id: 'rehearsals', label: 'Rehearsals', icon: Calendar },
              { id: 'hymns', label: 'Upcoming Hymns', icon: BookOpen },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Welcome Card */}
              <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-bold">
                    {member?.name?.charAt(0) || 'M'}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{member?.name}</h2>
                    <p className="text-purple-200">
                      {member?.voice_part} • {member?.instruments?.join(', ') || 'Vocalist'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <Calendar className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">{rehearsals.length}</p>
                  <p className="text-sm text-slate-500">Upcoming Rehearsals</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-amber-100">
                      <BookOpen className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">{upcomingServices.length}</p>
                  <p className="text-sm text-slate-500">Services This Week</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-green-100">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">Active</p>
                  <p className="text-sm text-slate-500">Member Status</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Mic2 className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-slate-800 capitalize">{member?.voice_part || '-'}</p>
                  <p className="text-sm text-slate-500">Voice Part</p>
                </div>
              </div>

              {/* Next Rehearsal */}
              {rehearsals.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                  <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    Next Rehearsal
                  </h3>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <div>
                      <p className="font-semibold text-slate-800">
                        {new Date(rehearsals[0].date).toLocaleDateString('en-GB', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long'
                        })}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                        {rehearsals[0].time && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {rehearsals[0].time}
                          </span>
                        )}
                        {rehearsals[0].location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {rehearsals[0].location}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-purple-400" />
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Rehearsals Tab */}
          {activeTab === 'rehearsals' && (
            <motion.div
              key="rehearsals"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    Rehearsal Schedule
                  </h3>
                  <button
                    onClick={loadChoirData}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
                
                {rehearsals.length === 0 ? (
                  <div className="p-12 text-center">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No upcoming rehearsals scheduled</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {rehearsals.map((rehearsal, idx) => (
                      <div key={idx} className="p-4 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-slate-800">
                              {new Date(rehearsal.date).toLocaleDateString('en-GB', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                            <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500">
                              {rehearsal.time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5" />
                                  {rehearsal.time}
                                </span>
                              )}
                              {rehearsal.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {rehearsal.location}
                                </span>
                              )}
                            </div>
                            {rehearsal.notes && (
                              <p className="text-sm text-slate-500 mt-2 flex items-start gap-1">
                                <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                {rehearsal.notes}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {rehearsal.songs_to_practice?.length > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                <Music2 className="w-3 h-3" />
                                {rehearsal.songs_to_practice.length} songs
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Hymns Tab */}
          {activeTab === 'hymns' && (
            <motion.div
              key="hymns"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-amber-600" />
                    Upcoming Service Hymns
                  </h3>
                </div>
                
                {upcomingServices.length === 0 ? (
                  <div className="p-12 text-center">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No hymn selections for upcoming services</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {upcomingServices.map((service, idx) => (
                      <div key={idx} className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-semibold text-slate-800 capitalize">
                              {service.service_type?.replace('_', ' ')} Service
                            </p>
                            <p className="text-sm text-slate-500">
                              {new Date(service.service_date).toLocaleDateString('en-GB', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long'
                              })}
                            </p>
                          </div>
                          <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                            {service.hymn_numbers?.length || 0} hymns
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {service.hymn_numbers?.map((num, i) => (
                            <span
                              key={i}
                              className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium"
                            >
                              Hymn No {num}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ChoirMemberPortal;
